import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Building, Home, MapPin, Bed, User, Mail, Phone, 
  Check, ArrowRight, ArrowLeft, Percent, Shield, Clock
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LandlordLeadForm = () => {
  const { isArabic } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    property_type: '',
    property_address: '',
    postcode: '',
    bedrooms: '',
    expected_rent: '',
    management_interest: true,
    additional_info: '',
    name: '',
    email: '',
    phone: ''
  });

  const propertyTypes = [
    { id: 'detached', label: isArabic ? 'منزل منفصل' : 'Detached House' },
    { id: 'semi-detached', label: isArabic ? 'شبه منفصل' : 'Semi-Detached' },
    { id: 'terraced', label: isArabic ? 'متلاصق' : 'Terraced' },
    { id: 'flat', label: isArabic ? 'شقة' : 'Flat / Apartment' },
    { id: 'studio', label: isArabic ? 'استوديو' : 'Studio' },
    { id: 'hmo', label: 'HMO' },
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/landlord`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          bedrooms: parseInt(formData.bedrooms) || 0,
          expected_rent: parseInt(formData.expected_rent) || 0
        })
      });
      
      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
    }
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#FF8C42] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-1 mx-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto px-4">
          <Card className="border-none shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#404040] mb-4">
                {isArabic ? 'شكراً لك!' : 'Thank You!'}
              </h2>
              <p className="text-lg text-gray-600 mb-2">
                {isArabic 
                  ? 'تم استلام استفسارك'
                  : 'Your enquiry has been received'
                }
              </p>
              <p className="text-gray-500 mb-8">
                {isArabic 
                  ? 'سيتواصل معك أحد مستشاري الإيجار لدينا خلال 24 ساعة'
                  : 'One of our lettings consultants will contact you within 24 hours'
                }
              </p>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white"
              >
                {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF8C42] rounded-full mb-4">
            <Building className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#404040]">
            {isArabic ? 'أجّر عقارك معنا' : 'Let Your Property With Us'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isArabic 
              ? 'خدمة إدارة شاملة بنسبة 12% فقط من الإيجار السنوي'
              : 'Full management service at just 12% of annual rent'
            }
          </p>
        </div>

        {/* Management Service Highlight */}
        <Card className="mb-8 bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className={`flex items-center gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Percent className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">12%</h3>
                  <p className="opacity-90">{isArabic ? 'رسوم الإدارة الشاملة' : 'Full Management Fee'}</p>
                </div>
              </div>
              <div className="text-sm opacity-90">
                <div className={`flex items-center gap-2 mb-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <Shield className="w-4 h-4" /> {isArabic ? 'فحص شامل للمستأجرين' : 'Full tenant vetting'}
                </div>
                <div className={`flex items-center gap-2 mb-1 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-4 h-4" /> {isArabic ? 'إدارة الصيانة على مدار الساعة' : '24/7 maintenance management'}
                </div>
                <div className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <Check className="w-4 h-4" /> {isArabic ? 'جمع الإيجار المضمون' : 'Guaranteed rent collection'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {renderStepIndicator()}

        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {/* Step 1: Property Type */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'نوع العقار' : 'Property Type'}
                  </h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {propertyTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({...formData, property_type: type.id})}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        formData.property_type === type.id
                          ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                          : 'border-gray-200 hover:border-[#FF8C42]/50'
                      }`}
                    >
                      <Home className={`w-8 h-8 mx-auto mb-2 ${formData.property_type === type.id ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
                      <p className="font-medium text-[#404040] text-sm">{type.label}</p>
                    </button>
                  ))}
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Bed className="w-5 h-5 text-[#FF8C42]" />
                    {isArabic ? 'عدد غرف النوم' : 'Number of Bedrooms'}
                  </Label>
                  <div className="flex gap-2">
                    {['1', '2', '3', '4', '5+'].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setFormData({...formData, bedrooms: num})}
                        className={`flex-1 py-3 rounded-lg border-2 transition-all font-semibold ${
                          formData.bedrooms === num
                            ? 'border-[#FF8C42] bg-[#FF8C42] text-white'
                            : 'border-gray-200 text-gray-600 hover:border-[#FF8C42]/50'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Property Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'تفاصيل العقار' : 'Property Details'}
                  </h2>
                </div>

                <div>
                  <Label htmlFor="property_address">{isArabic ? 'عنوان العقار' : 'Property Address'}</Label>
                  <div className="relative mt-1">
                    <MapPin className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="property_address"
                      placeholder={isArabic ? 'أدخل العنوان' : 'Enter address'}
                      value={formData.property_address}
                      onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="postcode">{isArabic ? 'الرمز البريدي' : 'Postcode'}</Label>
                  <Input
                    id="postcode"
                    placeholder="e.g., HA3 5AH"
                    value={formData.postcode}
                    onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                    className="mt-1 py-6"
                  />
                </div>

                <div>
                  <Label htmlFor="expected_rent">{isArabic ? 'الإيجار الشهري المتوقع (£)' : 'Expected Monthly Rent (£)'}</Label>
                  <Input
                    id="expected_rent"
                    type="number"
                    placeholder="e.g., 1500"
                    value={formData.expected_rent}
                    onChange={(e) => setFormData({...formData, expected_rent: e.target.value})}
                    className="mt-1 py-6"
                  />
                </div>

                {/* Management Interest */}
                <div className="p-4 bg-[#FF8C42]/10 rounded-xl">
                  <label className={`flex items-start gap-3 cursor-pointer ${isArabic ? 'flex-row-reverse' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.management_interest}
                      onChange={(e) => setFormData({...formData, management_interest: e.target.checked})}
                      className="w-5 h-5 mt-1 text-[#FF8C42] rounded"
                    />
                    <div>
                      <p className="font-semibold text-[#404040]">
                        {isArabic ? 'أرغب في خدمة الإدارة الشاملة' : 'I\'m interested in Full Management Service'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {isArabic 
                          ? '12% من إجمالي الإيجار السنوي - نحن نتولى كل شيء'
                          : '12% of annual rent - We handle everything'
                        }
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <Label htmlFor="additional_info">{isArabic ? 'معلومات إضافية (اختياري)' : 'Additional Information (optional)'}</Label>
                  <Textarea
                    id="additional_info"
                    placeholder={isArabic 
                      ? 'أخبرنا المزيد عن عقارك...'
                      : 'Tell us more about your property...'
                    }
                    value={formData.additional_info}
                    onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                    className="mt-1 min-h-[100px]"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Contact Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-[#404040]">
                    {isArabic ? 'معلومات الاتصال' : 'Contact Details'}
                  </h2>
                </div>

                <div>
                  <Label htmlFor="name">{isArabic ? 'الاسم الكامل' : 'Full Name'}</Label>
                  <div className="relative mt-1">
                    <User className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="name"
                      placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</Label>
                  <div className="relative mt-1">
                    <Mail className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="email"
                      type="email"
                      placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">{isArabic ? 'رقم الهاتف' : 'Phone Number'}</Label>
                  <div className="relative mt-1">
                    <Phone className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder={isArabic ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className={`flex justify-between mt-8 pt-6 border-t ${isArabic ? 'flex-row-reverse' : ''}`}>
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="px-6">
                  <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {isArabic ? 'السابق' : 'Previous'}
                </Button>
              ) : (
                <div></div>
              )}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-6"
                  disabled={currentStep === 1 && (!formData.property_type || !formData.bedrooms)}
                >
                  {isArabic ? 'التالي' : 'Next'}
                  <ArrowRight className={`w-5 h-5 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.phone}
                >
                  {isSubmitting ? (isArabic ? 'جاري الإرسال...' : 'Submitting...') : (isArabic ? 'إرسال الاستفسار' : 'Submit Enquiry')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
